import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BudgetTrackingTab = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('current-month');

  const budgetSummary = {
    totalBudget: 125000,
    spent: 87500,
    remaining: 37500,
    pendingApprovals: 12500
  };

  const expenseCategories = [
    {
      id: 1,
      category: "Personnel",
      budgeted: 75000,
      spent: 62500,
      percentage: 83,
      status: "on-track"
    },
    {
      id: 2,
      category: "Software & Tools",
      budgeted: 25000,
      spent: 18750,
      percentage: 75,
      status: "on-track"
    },
    {
      id: 3,
      category: "Training & Development",
      budgeted: 15000,
      spent: 4500,
      percentage: 30,
      status: "under-budget"
    },
    {
      id: 4,
      category: "Equipment",
      budgeted: 10000,
      spent: 1750,
      percentage: 18,
      status: "under-budget"
    }
  ];

  const pendingApprovals = [
    {
      id: 1,
      employee: "Kadiatou Bah",
      description: "Conference attendance - React Summit 2024",
      amount: 2500,
      category: "Training & Development",
      submittedDate: "2024-10-09",
      priority: "medium",
      receipts: 3
    },
    {
      id: 2,
      employee: "Ibrahima Camara",
      description: "Software license renewal - Figma Pro",
      amount: 1200,
      category: "Software & Tools",
      submittedDate: "2024-10-08",
      priority: "high",
      receipts: 1
    },
    {
      id: 3,
      employee: "Sékou Keïta",
      description: "Client dinner - Project kickoff meeting",
      amount: 450,
      category: "Business Meals",
      submittedDate: "2024-10-07",
      priority: "low",
      receipts: 2
    },
    {
      id: 4,
      employee: "Hawa Bangoura",
      description: "Testing equipment - Mobile devices",
      amount: 3200,
      category: "Equipment",
      submittedDate: "2024-10-06",
      priority: "high",
      receipts: 4
    }
  ];

  const handleApproval = (id, action) => {
    console.log(`${action} expense request ${id}`);
    // Approval logic here
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-error/10 text-error';
      case 'medium': return 'bg-warning/10 text-warning';
      case 'low': return 'bg-success/10 text-success';
      default: return 'bg-muted/10 text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on-track': return 'text-success';
      case 'over-budget': return 'text-error';
      case 'under-budget': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Budget Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center justify-between mb-2">
            <Icon name="DollarSign" size={20} className="text-primary" />
            <span className="text-xs text-muted-foreground">Total Budget</span>
          </div>
          <h3 className="text-2xl font-semibold text-foreground">
            {formatCurrency(budgetSummary?.totalBudget)}
          </h3>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center justify-between mb-2">
            <Icon name="TrendingDown" size={20} className="text-error" />
            <span className="text-xs text-muted-foreground">Spent</span>
          </div>
          <h3 className="text-2xl font-semibold text-foreground">
            {formatCurrency(budgetSummary?.spent)}
          </h3>
          <p className="text-xs text-muted-foreground">
            {Math.round((budgetSummary?.spent / budgetSummary?.totalBudget) * 100)}% of budget
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center justify-between mb-2">
            <Icon name="PiggyBank" size={20} className="text-success" />
            <span className="text-xs text-muted-foreground">Remaining</span>
          </div>
          <h3 className="text-2xl font-semibold text-foreground">
            {formatCurrency(budgetSummary?.remaining)}
          </h3>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 card-shadow">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={20} className="text-warning" />
            <span className="text-xs text-muted-foreground">Pending</span>
          </div>
          <h3 className="text-2xl font-semibold text-foreground">
            {formatCurrency(budgetSummary?.pendingApprovals)}
          </h3>
        </div>
      </div>
      {/* Expense Categories */}
      <div className="bg-card border border-border rounded-lg p-6 card-shadow">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Budget by Category</h3>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e?.target?.value)}
            className="px-3 py-1 border border-border rounded-md text-sm bg-input text-foreground"
          >
            <option value="current-month">Current Month</option>
            <option value="last-month">Last Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
        </div>

        <div className="space-y-4">
          {expenseCategories?.map((category) => (
            <div key={category?.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{category?.category}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">
                    {formatCurrency(category?.spent)} / {formatCurrency(category?.budgeted)}
                  </span>
                  <span className={`text-sm font-medium ${getStatusColor(category?.status)}`}>
                    {category?.percentage}%
                  </span>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-smooth ${
                    category?.percentage > 90 ? 'bg-error' :
                    category?.percentage > 75 ? 'bg-warning' : 'bg-success'
                  }`}
                  style={{ width: `${Math.min(category?.percentage, 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Pending Approvals */}
      <div className="bg-card border border-border rounded-lg p-6 card-shadow">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">
            Pending Approvals ({pendingApprovals?.length})
          </h3>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export Report
          </Button>
        </div>

        <div className="space-y-4">
          {pendingApprovals?.map((request) => (
            <div key={request?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-medium text-foreground">{request?.employee}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(request?.priority)}`}>
                      {request?.priority} priority
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{request?.description}</p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>Category: {request?.category}</span>
                    <span>Submitted: {request?.submittedDate}</span>
                    <span>{request?.receipts} receipt(s)</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-foreground mb-2">
                    {formatCurrency(request?.amount)}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleApproval(request?.id, 'approve')}
                      iconName="Check"
                      iconPosition="left"
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleApproval(request?.id, 'reject')}
                      iconName="X"
                      iconPosition="left"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BudgetTrackingTab;